import { useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Box, Button, Text, useColorModeValue, useDisclosure, VisuallyHidden } from "@chakra-ui/react";
import { LngLatBoundsLike } from "react-map-gl";
import { Feature } from "geojson";
import bbox from "@turf/bbox";

import { Loading, Error } from "../../components";
import { formatDate, getLikelySpecies } from "../../utils";
import { useModel } from "../../context/AppConfig";
import { useSceneInferences, useScene, useDebounce } from "../../hooks";
import { DEFAULT_BRIGHTNESS, DEFAULT_CONTRAST } from "../../settings";
import { PANEL_SIZE } from "../../theme";
import { ModelRun } from "../../types/scenes";
import ValidateMap from "./ValidationMap";
import ValidateHeader from "./ValidationHeader";
import ValidationPanel from "./ValidationPanel";
import useValidateNavigation from "./hooks/useValidateNavigation";
import useKeyboardShortcuts from "./hooks/useKeyboardShortcuts";
import { useInferenceFiltersContext } from "../../context";

// Shared layout for the Validate page's non-map states: not-found, fetch error, no
// inferences left to validate. Keeps copy + actions centered and readably narrow.
function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <Box
      display="grid"
      placeContent="center"
      gap="4"
      px="4"
      textAlign="center"
    >
      {children}
    </Box>
  );
}

function Validate() {
  const { sceneId, inferenceId } = useParams<{
    sceneId: string;
    inferenceId: string;
  }>();

  const {
    scene,
    error: sceneError,
    isPending: sceneIsPending,
    refetch: refetchScene,
  } = useScene(sceneId);

  const {
    isWhale,
    isValid,
    inferencesValidated,
    minConfidence,
    minCoastlineDistance,
    isFlagged,
    compositeMode,
    setCompositeMode,
    resetFilters,
  } = useInferenceFiltersContext();

  const DELAY = 2000;
  const debouncedIsWhale = useDebounce(isWhale, DELAY);
  const debouncedIsValid = useDebounce(isValid, DELAY);
  const debouncedInferencesValidated = useDebounce(inferencesValidated, DELAY);
  const debouncedMinConfidence = useDebounce(minConfidence, DELAY);
  const debouncedMinCoastlineDistance = useDebounce(minCoastlineDistance, DELAY);
  const debouncedIsFlagged = useDebounce(isFlagged, DELAY);
  const debouncedCompositeMode = useDebounce(compositeMode, DELAY);

  const {
    inferences,
    error: inferencesError,
    isPending: inferencesIsPending,
    isValidating,
    validationError,
    validate,
    isUpdating,
    updateError,
    updateInference,
    refetch: refetchInferences,
  } = useSceneInferences(sceneId, {
    is_whale: debouncedIsWhale,
    min_coastline_distance: debouncedMinCoastlineDistance,
    min_confidence: debouncedMinConfidence,
    is_valid: debouncedIsValid,
    validated: debouncedInferencesValidated,
    composited: debouncedCompositeMode,
    flagged: debouncedIsFlagged,
  });

  const {
    inferencesSorted,
    currentIndex,
    selectedInference,
    nextInferenceId,
    previousInferenceId,
  } = useValidateNavigation({ sceneId, inferenceId, inferences, inferencesIsPending, compositeMode });

  const [brightness, setBrightness] = useState<number>(DEFAULT_BRIGHTNESS);
  const [contrast, setContrast] = useState<number>(DEFAULT_CONTRAST);
  const [bboxMask, setBboxMask] = useState<boolean>(true);

  const {
    isOpen: shortcutsModalIsOpen,
    onOpen: onShortcutsModalOpen,
    onClose: onShortcutsModalClose,
  } = useDisclosure();

  const { announcement } = useKeyboardShortcuts({
    sceneId: sceneId || "",
    selectedInference,
    compositeMode,
    setCompositeMode,
    currentIndex,
    inferencesSortedLength: inferencesSorted.length,
    nextInferenceId,
    previousInferenceId,
    isValidating,
    isUpdating,
    validate,
    updateInference,
    disabled: shortcutsModalIsOpen,
  });

  const model = useModel(selectedInference?.properties.model_id);

  const bounds: LngLatBoundsLike | undefined = useMemo(() => {
    if (!selectedInference) return;
    const [x1, y1, x2, y2] = bbox(selectedInference as Feature);
    return [x1, y1, x2, y2];
  }, [selectedInference]);

  const borderColor = useColorModeValue("base.100a", "base.500a");

  if (sceneIsPending || inferencesIsPending) {
    return (
      <Loading mt="8">
        {sceneIsPending ? "Loading scene..." : "Loading detections..."}
      </Loading>
    );
  }

  if (!sceneId) {
    return (
      <EmptyState>
        <Error mb="0">Scene not found</Error>
        <Text fontSize="sm" opacity="0.75" maxW="md">
          The link you followed may be broken, or the scene may have been
          moved or deleted.
        </Text>
        <Button size="sm" variant="base-soft-outline" as={Link} to="/scenes">
          Back to Scenes
        </Button>
      </EmptyState>
    );
  }

  if (sceneError || inferencesError) {
    return (
      <EmptyState>
        <Error mb="0">Something went wrong</Error>
        <Text fontSize="sm" opacity="0.75" maxW="md">
          {sceneError?.message ||
            inferencesError?.message ||
            "The scene or its detections couldn't be loaded."}
        </Text>
        <Box display="flex" gap="3" alignItems="center" justifyContent="center">
          <Button
            size="sm"
            variant="primary-soft-outline"
            onClick={() => {
              refetchScene();
              refetchInferences();
            }}
          >
            Try Again
          </Button>
          <Button size="sm" variant="base-soft-outline" as={Link} to="/scenes">
            Back to Scenes
          </Button>
        </Box>
      </EmptyState>
    );
  }

  const hasSuccessModelRuns = scene.properties.model_runs.some(
    ({ status }: ModelRun) => status === "succeeded",
  );
  const filtersActive =
    isWhale !== undefined ||
    isValid !== undefined ||
    inferencesValidated !== undefined ||
    minConfidence > 0.1 ||
    minCoastlineDistance > 40 ||
    isFlagged !== undefined;

  if (!inferences || inferences.features.length === 0) {
    return (
      <EmptyState>
        {!hasSuccessModelRuns ? (
          <>
            <Error mb="0">No completed model runs</Error>
            <Text fontSize="sm" opacity="0.75" maxW="md">
              This scene doesn&rsquo;t have any successful model runs yet, so
              there&rsquo;s nothing to validate. Run a model from the scene
              page, then come back here.
            </Text>
            <Button
              size="sm"
              variant="base-soft-outline"
              as={Link}
              to={`/scenes/${sceneId}`}
            >
              Back to Scene
            </Button>
          </>
        ) : filtersActive ? (
          <>
            <Error mb="0">No inferences match the current filters</Error>
            <Text fontSize="sm" opacity="0.75" maxW="md">
              This scene may have detections hidden by the active filters.
              Try resetting the filters, or head back to the scene page.
            </Text>
            <Box display="flex" gap="3">
              <Button
                size="sm"
                variant="primary-soft-outline"
                onClick={resetFilters}
              >
                Reset Filters
              </Button>
              <Button
                size="sm"
                variant="base-soft-outline"
                as={Link}
                to={`/scenes/${sceneId}`}
              >
                Back to Scene
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Error mb="0">No detections to validate</Error>
            <Text fontSize="sm" opacity="0.75" maxW="md">
              This scene has no detections left to validate.
            </Text>
            <Button
              size="sm"
              variant="base-soft-outline"
              as={Link}
              to={`/scenes/${sceneId}`}
            >
              Back to Scene
            </Button>
          </>
        )}
      </EmptyState>
    );
  }

  if (!selectedInference) {
    return <Loading mt="8">Selecting inference...</Loading>;
  }

  const sceneDate = formatDate(new Date(Date.parse(scene.properties.date)));
  const { species, confidence } = getLikelySpecies(selectedInference);
  const isValidated =
    selectedInference.properties.is_valid !== undefined &&
    selectedInference.properties.is_valid !== null;

  return (
    <Box display="grid" gridTemplateRows="max-content 1fr" maxH="calc(100vh - 2.81rem)">
      <VisuallyHidden aria-live="polite" role="status">
        {announcement}
      </VisuallyHidden>
      <ValidateHeader
        borderColor={borderColor}
        sceneId={sceneId}
        sceneDate={sceneDate}
        currentIndex={currentIndex}
        inferencesSortedLength={inferencesSorted.length}
        nextInferenceId={nextInferenceId}
        previousInferenceId={previousInferenceId}
        isValidating={isValidating}
        brightness={brightness}
        setBrightness={setBrightness}
        contrast={contrast}
        setContrast={setContrast}
        bboxMask={bboxMask}
        setBboxMask={setBboxMask}
        compositeMode={compositeMode}
        setCompositeMode={setCompositeMode}
        shortcutsModalIsOpen={shortcutsModalIsOpen}
        onShortcutsModalOpen={onShortcutsModalOpen}
        onShortcutsModalClose={onShortcutsModalClose}
      />
      <Box
        display="grid"
        gridTemplateColumns={`1fr ${PANEL_SIZE}`}
        overflow="hidden"
      >
        <ValidateMap
          sceneId={sceneId}
          brightness={brightness}
          contrast={contrast}
          bboxMask={bboxMask}
          compositeMode={compositeMode}
          bounds={bounds}
          selectedInference={selectedInference}
          inferences={inferences}
          isValidated={isValidated}
        />
        <ValidationPanel
          borderColor={borderColor}
          scene={scene}
          sceneDate={sceneDate}
          model={model}
          selectedInference={selectedInference}
          species={species}
          confidence={confidence}
          validate={validate}
          isValidating={isValidating}
          validationError={validationError}
          isValidated={isValidated}
          updateInference={updateInference}
          isUpdating={isUpdating}
          updateError={updateError}
          compositeMode={compositeMode}
        />
      </Box>
    </Box>
  );
}

export default Validate;
